import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import InvoiceModal from '../components/InvoiceModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import PaymentModal from '../components/PaymentModal';
import {
  fetchInvoices,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  addPayment,
  sendInvoice,
} from '../store/slices/invoicesSlice';
import { fetchProducts } from '../store/slices/productsSlice';
import { apiService } from '../services/api';
import './Invoices.css';

const Invoices = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Redux state
  const { invoices, loading, error, total, sendLoading } = useSelector(state => state.invoices);
  const { items: rawProducts } = useSelector(state => state.products);

  // Map real catalog products to the shape InvoiceModal expects: { id, name, price, sku }
  const products = rawProducts.map(p => ({
    id: p._id,
    name: p.name,
    price: p.price ?? p.salePrice ?? 0,
    sku: p.retailerId || p.sku || '',
  }));

  // Local UI state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [invoiceForPayment, setInvoiceForPayment] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [pdfLoading, setPdfLoading] = useState({});   // { [invoiceId]: 'download' | 'preview' | false }
  const [sendingInvoiceId, setSendingInvoiceId] = useState(null);

  const statuses = ['all', 'draft', 'sent', 'pending', 'partial', 'paid', 'overdue', 'cancelled'];

  // Fetch invoices and catalog products on component mount
  useEffect(() => {
    dispatch(fetchProducts({ limit: 200 }))
      .unwrap()
      .catch(err => console.error('Failed to fetch products:', err));
  }, [dispatch]);

  // Re-fetch from server when search/status filter changes
  useEffect(() => {
    const params = { page: 1, limit: 20 };
    if (searchQuery) params.search = searchQuery;
    if (selectedStatus !== 'all') params.status = selectedStatus;
    dispatch(fetchInvoices(params))
      .unwrap()
      .catch(err => console.error('Failed to fetch invoices:', err));
  }, [dispatch, searchQuery, selectedStatus]);



  const filteredInvoices = invoices;

  const handleCreateInvoice = () => {
    setEditingInvoice(null);
    setIsModalOpen(true);
  };

  const handleViewInvoice = (invoice) => {
    navigate('/invoices/' + invoice._id);
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setIsModalOpen(true);
  };

  const handleDeleteInvoice = (invoice) => {
    setInvoiceToDelete(invoice);
    setIsDeleteModalOpen(true);
  };

  const handleSaveInvoice = async (formData) => {
    try {
      if (editingInvoice) {
        await dispatch(updateInvoice({
          id: editingInvoice._id,
          data: formData
        })).unwrap();
        toast.success('Invoice updated successfully', { position: 'bottom-right' });
      } else {
        await dispatch(createInvoice(formData)).unwrap();
        toast.success('Invoice created successfully', { position: 'bottom-right' });
      }
      setIsModalOpen(false);
      setEditingInvoice(null);
    } catch (err) {
      console.error('Failed to save invoice:', err);
      toast.error(err?.message || 'Failed to save invoice', { position: 'bottom-right' });
    }
  };

  const handleConfirmDelete = async () => {
    if (invoiceToDelete) {
      try {
        await dispatch(deleteInvoice(invoiceToDelete._id)).unwrap();
        toast.success('Invoice deleted', { position: 'bottom-right' });
        setIsDeleteModalOpen(false);
        setInvoiceToDelete(null);
      } catch (err) {
        console.error('Failed to delete invoice:', err);
        toast.error(err?.message || 'Failed to delete invoice', { position: 'bottom-right' });
      }
    }
  };

  const handleAddPayment = (invoice) => {
    setInvoiceForPayment(invoice);
    setIsPaymentModalOpen(true);
  };

  const handleSavePayment = async (paymentData) => {
    if (invoiceForPayment) {
      try {
        await dispatch(addPayment({
          invoiceId: invoiceForPayment._id,
          paymentData
        })).unwrap();
        toast.success('Payment recorded successfully', { position: 'bottom-right' });
        setIsPaymentModalOpen(false);
        setInvoiceForPayment(null);
      } catch (err) {
        console.error('Failed to add payment:', err);
        toast.error(err?.message || 'Failed to add payment', { position: 'bottom-right' });
      }
    }
  };

  // ── Server-side PDF helpers ──────────────────────────────────────────────
  const triggerBlobDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadPDF = async (invoice) => {
    setPdfLoading(prev => ({ ...prev, [invoice._id]: 'download' }));
    try {
      const response = await apiService.invoices.downloadPDF(invoice._id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      triggerBlobDownload(blob, `Invoice-${invoice.invoiceNumber || invoice._id}.pdf`);
      toast.success('PDF downloaded', { position: 'bottom-right' });
    } catch (err) {
      console.error('PDF download error:', err);
      toast.error('Failed to generate PDF. Please try again.', { position: 'bottom-right' });
    } finally {
      setPdfLoading(prev => ({ ...prev, [invoice._id]: false }));
    }
  };

  const handlePreviewPDF = async (invoice) => {
    setPdfLoading(prev => ({ ...prev, [invoice._id]: 'preview' }));
    try {
      const response = await apiService.invoices.previewPDF(invoice._id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error('PDF preview error:', err);
      toast.error('Failed to preview PDF. Please try again.', { position: 'bottom-right' });
    } finally {
      setPdfLoading(prev => ({ ...prev, [invoice._id]: false }));
    }
  };
  // ────────────────────────────────────────────────────────────────────────

  const handleSendInvoice = async (invoice) => {
    const phoneNumberId = localStorage.getItem('phoneNumberId');
    const recipientPhone = invoice.billTo?.phone;

    
    if (!recipientPhone) {
      toast.error('Invoice has no recipient phone number. Please update the invoice.', { position: 'bottom-right' });
      return;
    }

    setSendingInvoiceId(invoice._id);
    try {
      await dispatch(sendInvoice({
        invoiceId: invoice._id,
        phoneNumberId,
        recipientPhone
      })).unwrap();
      toast.success(`Invoice ${invoice.invoiceNumber} sent via WhatsApp`, { position: 'bottom-right' });
    } catch (err) {
      const msg = err?.message || err || 'Failed to send invoice';
      toast.error(msg, { position: 'bottom-right' });
    } finally {
      setSendingInvoiceId(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(value || 0);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'draft': { color: '#6b7280', bg: '#f3f4f6' },
      'sent': { color: '#3b82f6', bg: '#dbeafe' },
      'pending': { color: '#8b5cf6', bg: '#ede9fe' },
      'partial': { color: '#f59e0b', bg: '#fef3c7' },
      'paid': { color: '#10b981', bg: '#d1fae5' },
      'overdue': { color: '#ef4444', bg: '#fee2e2' },
      'cancelled': { color: '#6b7280', bg: '#e5e7eb' }
    };

    const config = statusConfig[status?.toLowerCase()] || statusConfig['draft'];
    return (
      <span 
        className="status-badge" 
        style={{ 
          background: config.bg, 
          color: config.color 
        }}
      >
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date() && new Date(dueDate).toDateString() !== new Date().toDateString();
  };

  if (loading && invoices.length === 0) {
    return (
      <div className="invoices-container">
        <div className="loading-state" style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner"></div>
          <p>Loading invoices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="invoices-container">
      <div className="invoices-header">
        <div className="header-content">
          <h1>Invoices</h1>
          <p>Create, manage, and send invoices to your customers</p>
        </div>
        <button className="btn-create-invoice" onClick={handleCreateInvoice}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Create Invoice
        </button>
      </div>

      <div className="invoices-toolbar">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search by invoice number, customer name, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="filter-select"
          >
            {statuses.map(status => (
              <option key={status} value={status}>
                {status === 'all' ? 'All Status' : status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div className="invoices-stats">
          <span className="stat-item">
            Total: <strong>{total || invoices.length}</strong>
          </span>
          <span className="stat-item">
            Paid: <strong>{invoices.filter(i => i.status === 'paid').length}</strong>
          </span>
          <span className="stat-item">
            Overdue: <strong>{invoices.filter(i => i.status === 'overdue').length}</strong>
          </span>
          <span className="stat-item">
            Outstanding: <strong>{formatCurrency(invoices.reduce((sum, inv) => sum + (inv.total - (inv.paidAmount || 0)), 0))}</strong>
          </span>
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ padding: '10px', marginBottom: '10px', background: '#fee2e2', color: '#d32f2f', borderRadius: '4px' }}>
          {error?.message || 'Error loading invoices'}
        </div>
      )}

      <div className="invoices-table-container">
        <table className="invoices-table">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Customer</th>
              <th>Date</th>
              <th>Due Date</th>
              <th>Total</th>
              <th>Paid</th>
              <th>Balance</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.length === 0 ? (
              <tr>
                <td colSpan="9" className="empty-state-cell">
                  <div className="empty-state">
                    <div className="empty-icon">📄</div>
                    <h3>No invoices found</h3>
                    <p>Create your first invoice to get started</p>
                    <button className="btn-add-first" onClick={handleCreateInvoice}>
                      Create Invoice
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              filteredInvoices.map(invoice => (
                <tr key={invoice._id} className={isOverdue(invoice.dueDate) && invoice.status !== 'paid' ? 'overdue-row' : ''}>
                  <td>
                    <strong>{invoice.invoiceNumber}</strong>
                  </td>
                  <td>
                    <div className="customer-cell">
                      <div className="customer-name">{invoice.billTo?.name || 'N/A'}</div>
                      <div className="customer-phone">{invoice.billTo?.phone || 'N/A'}</div>
                    </div>
                  </td>
                  <td>{formatDate(invoice.invoiceDate)}</td>
                  <td className={isOverdue(invoice.dueDate) && invoice.status !== 'paid' ? 'overdue-date' : ''}>
                    {formatDate(invoice.dueDate)}
                  </td>
                  <td className="amount-cell">{formatCurrency(invoice.total)}</td>
                  <td className="amount-cell">{formatCurrency(invoice.paidAmount)}</td>
                  <td className="amount-cell balance-cell">
                    <strong>{formatCurrency(invoice.total - (invoice.paidAmount || 0))}</strong>
                  </td>
                  <td>{getStatusBadge(invoice.status)}</td>
                  <td>
                    <div className="invoice-actions">
                      {invoice.status === 'draft' ? (
                        <>
                          {/* Draft: Edit + Send + Delete only */}
                          <button className="action-btn edit-btn" onClick={() => handleEditInvoice(invoice)} title="Edit Invoice">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                          <button className="action-btn send-btn" onClick={() => handleSendInvoice(invoice)} disabled={sendingInvoiceId === invoice._id} title="Send via WhatsApp" style={{ opacity: sendingInvoiceId === invoice._id ? 0.5 : 1 }}>
                            {sendingInvoiceId === invoice._id ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ animation: 'spin 1s linear infinite' }}>
                                <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                              </svg>
                            ) : (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                              </svg>
                            )}
                          </button>
                          <button className="action-btn delete-btn" onClick={() => handleDeleteInvoice(invoice)} title="Delete Invoice">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            </svg>
                          </button>
                        </>
                      ) : (
                        <>
                          {/* Non-draft: all buttons */}
                          <button className="action-btn view-btn" onClick={() => handleViewInvoice(invoice)} title="View Invoice">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                            </svg>
                          </button>
                          <button className="action-btn edit-btn" onClick={() => handleEditInvoice(invoice)} title="Edit Invoice">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                          <button className="action-btn pdf-btn" onClick={() => handleDownloadPDF(invoice)} disabled={!!pdfLoading[invoice._id]} title="Download PDF" style={{ opacity: pdfLoading[invoice._id] ? 0.5 : 1 }}>
                            {pdfLoading[invoice._id] ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ animation: 'spin 1s linear infinite' }}>
                                <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                              </svg>
                            ) : (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 18 15 15"/>
                              </svg>
                            )}
                          </button>
                          {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                            <button className="action-btn payment-btn" onClick={() => handleAddPayment(invoice)} title="Add Payment">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                              </svg>
                            </button>
                          )}
                          {(invoice.status === 'sent' || invoice.status === 'pending') && (
                            <button className="action-btn send-btn" onClick={() => handleSendInvoice(invoice)} disabled={sendingInvoiceId === invoice._id} title="Resend via WhatsApp" style={{ opacity: sendingInvoiceId === invoice._id ? 0.5 : 1 }}>
                              {sendingInvoiceId === invoice._id ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ animation: 'spin 1s linear infinite' }}>
                                  <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                                </svg>
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
                                </svg>
                              )}
                            </button>
                          )}
                          {(invoice.status === 'sent' || invoice.status === 'pending') && (
                            <button className="action-btn delete-btn" onClick={() => handleDeleteInvoice(invoice)} title="Delete Invoice">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                              </svg>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <InvoiceModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingInvoice(null);
        }}
        onSave={handleSaveInvoice}
        invoice={editingInvoice}
        products={products}
      />

      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => {
          setIsPaymentModalOpen(false);
          setInvoiceForPayment(null);
        }}
        onSave={handleSavePayment}
        invoice={invoiceForPayment}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setInvoiceToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        customerName={invoiceToDelete?.invoiceNumber || ''}
        type="invoice"
      />
    </div>
  );
};

export default Invoices;


